import type { SidecarRecord } from '@/types/intake';

/** Records with only the legacy printDispatch (no Send batch) collapse into one synthetic bucket. */
export const LEGACY_BATCH_ID = 'Earlier (pre-batch)';

/** One "approval stack": a send batch and the records that went out in it. */
export interface BatchGroup {
  batchId: string;
  /** Newest sent/dispatch timestamp in the batch, used to order stacks (newest first). */
  latestSentAt: string;
  /** Who pressed Send for this batch (one press = one sender): sentByName, else sentByEmail, else null.
   * Older records without the identity stamp (pre-backfill) leave it null and the header shows nothing. */
  sender: string | null;
  /** True for the synthetic legacy bucket (records that predate the Send batch flow). */
  isLegacy: boolean;
  records: SidecarRecord[];
}

/** Sent date for a single record: the Send batch stamp, else the legacy OK-to-print stamp. */
export function recordSentAt(r: SidecarRecord): string | null {
  return r.sent?.sentAt ?? r.printDispatch?.dispatchedAt ?? null;
}

/** Short "3 neg, 1 pos, 1 new kit" mix from the records in a batch. */
export function resultMix(records: SidecarRecord[]): string {
  let neg = 0;
  let pos = 0;
  let kit = 0;
  for (const r of records) {
    if (r.result === 'Negative') neg += 1;
    else if (r.result === 'Positive') pos += 1;
    else kit += 1; // Unable to test -> re-do kit
  }
  const parts: string[] = [];
  if (neg) parts.push(`${neg} neg`);
  if (pos) parts.push(`${pos} pos`);
  if (kit) parts.push(`${kit} new kit`);
  return parts.join(', ');
}

/** The batch's sender display string off the first record carrying the identity stamp. */
function batchSender(records: SidecarRecord[]): string | null {
  for (const r of records) {
    const who = r.sent?.sentByName || r.sent?.sentByEmail;
    if (who) return who;
  }
  return null;
}

/** Group records into batches (newest batch first), bucketing legacy print-only records together. */
export function groupIntoBatches(records: SidecarRecord[]): BatchGroup[] {
  const byBatch = new Map<string, SidecarRecord[]>();
  for (const r of records) {
    const key = r.sent?.batchId ?? LEGACY_BATCH_ID;
    const bucket = byBatch.get(key);
    if (bucket) bucket.push(r);
    else byBatch.set(key, [r]);
  }
  const groups: BatchGroup[] = [];
  for (const [batchId, recs] of byBatch) {
    const latestSentAt = recs.reduce<string>((acc, r) => {
      const ts = recordSentAt(r) ?? '';
      return ts > acc ? ts : acc;
    }, '');
    groups.push({
      batchId,
      latestSentAt,
      sender: batchSender(recs),
      isLegacy: batchId === LEGACY_BATCH_ID,
      records: recs,
    });
  }
  // Newest batch first by latest send timestamp; the legacy bucket sinks to the bottom on ties/empties.
  groups.sort((a, b) => b.latestSentAt.localeCompare(a.latestSentAt));
  return groups;
}
